import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { decode } from 'jsonwebtoken';
import OtpInput from 'react-otp-input';
import { QRCodeSVG } from 'qrcode.react';
import { PublicClientApplication } from '@azure/msal-browser';
import AnonymousLayout from '../../layouts/AnonymousLayout';
import AppApi from '../../../core/AppApi';
import AuthenticationResult, { IdName } from '../../../models/be/AuthenticationResult';
import { Logo } from '../../../models/be/Branding';
import Loading from '../../../commonComponents/Loading';
import LoginForm from './LoginForm';
import ToastContainer from '../../../commonComponents/ToastContainer';
import { authenticate, checkOTPCode, getUserLoginAttempts, validateCaptchaValue } from '../../../core/api';
import { bullhorn, candidateRole, customerRole, jobAdder } from '../../../core/constants';
import {
	emailValidationRegex,
	getQueryParam,
	getUniqueQueryString,
	hasValue,
	redirectOrChangePage
} from '../../../lib/utils';
import {
	saveTokens,
	clearSetPasswordQueryStringsInLocalStorage,
	clearInvitationQueryStringsInLocalStorage,
	CustomerJwtPayload,
	sessionKeys
} from '../../../core/session';
import './Login.less';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import { LoginStep } from '../../../core/enums';
import { getBrandingLogo } from '../../../core/customer';
import {
	UserLoginAttemptsDetails,
	createDefaultUserLoginAttemptsDetails
} from '../../../models/be/transport/UserLoginAttemptsDetails';
import { CustomerSsoDetails } from '../../../models/be/SsoCustomer';
import { getSsoClientToken, getSsoCustomersByDomain } from '../../../core/CustomerSsoApi';
import SsoClientCredentials from '../../../models/be/transport/SsoClientCredentials';
import CheckBox from '../../common/checkDetails/CheckBox';

const LoginContainer = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const captchaRef = useRef(null);
	const [showPassword, setShowPassword] = useState(false);
	const [email, setEmail] = useState<string>(decodeURIComponent(getUniqueQueryString(location.search, 'email')));
	const [password, setPassword] = useState<string>('');
	const [rememberMe, setRememberMe] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [sending, setSending] = useState<boolean>(false);
	const [showCustomerSelection, setShowCustomerSelection] = useState<boolean>(false);
	const [customers, setCustomers] = useState<IdName[] | null>(null);
	const [customer, setCustomer] = useState<IdName | null>(null);
	const [refreshToken, setRefreshToken] = useState<string | null>(null);
	const [loginStep, setLoginStep] = useState<LoginStep>(LoginStep.Login);
	const [otp, setOtp] = useState<string>('');
	const [otpErrorMessage, setOtpErrorMessage] = useState<string>('');
	const [otpTryTimes, setOtpTryTimes] = useState<number>(0);
	const [otpSeconds, setOtpSeconds] = useState<number>(90);
	const [userLoginAttempts, setUserLoginAttempts] = useState<UserLoginAttemptsDetails>(
		createDefaultUserLoginAttemptsDetails
	);
	const [otpAuthUrl, setOtpAuthUrl] = useState<string>('');
	const [logo, setLogo] = useState<Logo | null>(null);
	const [recaptchaValue, setRecaptchaValue] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (location.search.includes(bullhorn) || location.search.includes(jobAdder)) {
			setRedirectAppUrl();
		}
		getWebsiteLogo();
		if (location.search.includes('sessionExpired=true')) toast('Session has ended, please log in again.');
	}, []);

	if (loading) {
		return <Loading />;
	}

	const handleLogin = async () => {
		if (!hasValue(email)) {
			setErrorMessage('Email is required');
			setSending(false);
			return;
		}
		setErrorMessage(null);

		setLoading(true);
		try {
			const ssoCustomer = await getssoCustomer();
			if (ssoCustomer) {
				const config = {
					auth: {
						clientId: ssoCustomer.clientId,
						authority: ssoCustomer.authority,
						redirectUri: '/'
					},
					cache: {
						cacheLocation: 'sessionStorage'
					}
				};

				const msalInstance = new PublicClientApplication(config);
				await msalInstance.initialize();
				const loginResponse = await msalInstance.acquireTokenPopup({
					scopes: [ssoCustomer.scope]
				});
				if (loginResponse.account && loginResponse.account.username?.toLowerCase() === email?.toLowerCase()) {
					const ssoClientCredentials: SsoClientCredentials = {
						grantType: 'sso_clients',
						ssoClientId: ssoCustomer.clientId,
						username: email?.toLowerCase(),
						rememberMe
					};
					const token = await getSsoClientToken(ssoClientCredentials);
					saveTokens(token.refreshToken, token.accessToken);
					const redirectUrl = getRedirectAppUrl();
					if (redirectUrl) {
						removeRedirectAppUrl();
						navigate(redirectUrl);
					} else navigate('/candidates');
				}
			} else {
				setShowPassword(true);
			}
		} catch (error) {
			if (error?.errorCode === 'user_cancelled') {
				navigate('/');
			} else if (error?.errorMessage?.includes('Error opening popup window')) {
				setErrorMessage('Please enable popups in your browser in order to login');
				navigate('/');
			} else throw new Error(error);
		}
		setLoading(false);
	};

	const getRedirectAppUrl = () => {
		return localStorage.getItem(sessionKeys.redirectAppUrl);
	};

	const removeRedirectAppUrl = () => {
		return localStorage.removeItem(sessionKeys.redirectAppUrl);
	};

	const setRedirectAppUrl = () => {
		const redirectAppUrlFromLocalStorage = getQueryParam(sessionKeys.redirectAppUrl);
		if (hasValue(redirectAppUrlFromLocalStorage) && redirectAppUrlFromLocalStorage !== '\\') {
			localStorage.setItem(sessionKeys.redirectAppUrl, redirectAppUrlFromLocalStorage);
		}
	};

	const onSubmit = async () => {
		if (!hasValue(email)) {
			setErrorMessage('Email is required');
			setSending(false);
			return;
		}
		setErrorMessage(null);

		setSending(true);
		const totalAttempts = await getUserLoginAttempts(email);
		setUserLoginAttempts(totalAttempts);
		if (totalAttempts.invalidLoginCount > 2) {
			if (!hasValue(recaptchaValue)) {
				setErrorMessage('Please check the reCAPTCHA.');
				setSending(false);
				return;
			}

			const result = await validateCaptchaValue(recaptchaValue);
			if (result !== null && result.errorMessage !== null) {
				setErrorMessage(result.errorMessage);
				setSending(false);
				return;
			}
		}

		const authenticationData = await validateCredentials();
		handleLogedIn(authenticationData);
	};

	const getWebsiteLogo = async () => {
		setSending(true);
		const brandingLogo = await getBrandingLogo();
		setLogo(brandingLogo);
		setSending(false);
	};

	const getssoCustomer = async () => {
		setSending(true);
		const emailDomain = email.split('@')[1]?.toLowerCase().trim();
		const ssoCustomers = await getSsoCustomersByDomain(emailDomain?.trim());
		if (emailDomain && ssoCustomers) {
			const customersWithSsoProperties = ssoCustomers.filter(
				(ssoCustomer) => ssoCustomer.ssoProperties !== undefined && ssoCustomer.ssoProperties !== ''
			);
			const customersWithDomains: CustomerSsoDetails[] = customersWithSsoProperties.map((ssoCustomer) => {
				const parsedProperties = JSON.parse(ssoCustomer.ssoProperties);
				return {
					customerId: ssoCustomer.customerId,
					customerName: ssoCustomer.customerName,
					scope: parsedProperties.scope,
					clientId: parsedProperties.clientId,
					domain: parsedProperties.domain,
					authority: parsedProperties.authority
				};
			});
			setSending(false);
			return customersWithDomains.find((x) => x.domain.includes(emailDomain));
		}
		return null;
	};

	const onHandleOTPCodeChange = async (otpValue: string) => {
		setOtp(otpValue);
		setOtpErrorMessage('');
		if (otpValue.length === 6) {
			setSending(true);
			const authenticationData = await checkOTPCode(email, password, otpValue);
			setSending(false);
			handleLogedIn(authenticationData);
		}
	};

	const handleOTPCodeIncorrect = () => {
		setOtpTryTimes((prev) => prev + 1);
		setOtp('');
		if (otpTryTimes >= 3) {
			setOtpErrorMessage('');
			setOtpTryTimes(0);
			setLoginStep(LoginStep.Login);
			navigate('/candidates');
		}
	};

	const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value.trim());
	};
	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(e.target.value);
	};

	const handleRememberChange = (value: boolean) => {
		setRememberMe(value);
	};

	const handleCustomerChange = async (e: DropDownListChangeEvent) => {
		setCustomer(e.target.value);
	};

	const handleCustomerClick = async () => {
		setSending(true);
		if (refreshToken === null) {
			throw new Error('refreshToken is null.');
		}
		const accessToken = await AppApi.generateCandidateAccessToken(email, refreshToken, customer?.id);
		saveTokensAndChangePage(refreshToken, accessToken);
	};

	const handleLogedIn = async (authenticationData: AuthenticationResult) => {
		if (
			authenticationData &&
			(authenticationData.errorMessage === undefined || authenticationData.errorMessage === null)
		) {
			const {
				refreshToken: refreshTokenFromData,
				accessToken,
				customers: customersFromData,
				otpAuthUrl: otpAuthUrlFromData
			} = authenticationData;
			if (otpAuthUrlFromData !== null) {
				if (otpAuthUrlFromData === 'OTPCode') {
					// this.setState({ remembered: true });
					goToOtpInput();
				} else {
					setLoginStep(LoginStep.QRCode);
					setOtpAuthUrl(`otpauth://totp/${otpAuthUrlFromData}`);
				}
			} else if (accessToken === null) {
				// This must be a Candidate with multiple customers.
				if (customersFromData === null) {
					throw new Error('customers can not be null when the access token is null.');
				} else if (customersFromData.length === 1) {
					throw new Error('customers can not be one when the access token is null.');
				} else {
					setCustomers(customersFromData);
					setRefreshToken(refreshTokenFromData);
					setShowCustomerSelection(true);
					setCustomer(customersFromData[0]);
				}
			} else {
				clearSetPasswordQueryStringsInLocalStorage();
				clearInvitationQueryStringsInLocalStorage();
				saveTokensAndChangePage(refreshTokenFromData, accessToken);
			}
		} else if (loginStep === LoginStep.OTP) {
			setOtpErrorMessage(authenticationData.errorMessage);
			handleOTPCodeIncorrect();
		}
	};

	const validateCredentials = async (): Promise<AuthenticationResult> => {
		if (emailValidationRegex.test(email) && password) {
			try {
				const authenticationData = await authenticate(email, password, rememberMe);
				setSending(false);
				if (authenticationData.errorMessage === undefined || authenticationData.errorMessage === null) {
					return authenticationData;
				}
				setErrorMessage(authenticationData.errorMessage);
			} catch (e) {
				setErrorMessage('Unexpected error when logging in. Please try again later.');
				setSending(false);
			}
		} else {
			setErrorMessage('Incorrect email or password.');
			setSending(false);
		}
		return null;
	};

	const saveTokensAndChangePage = (refreshTokenValue: string, accessToken: string) => {
		const decodedAccessToken = decode(accessToken) as CustomerJwtPayload;
		const redirectUrl = new URL(window.location.href).searchParams.get('redirectAppUrl');
		const companyId = new URL(window.location.href).searchParams.get('companyId');
		switch (decodedAccessToken.role) {
			case customerRole:
				if (refreshTokenValue === null) {
					throw new Error('refreshToken can not be not null for Customers when accessToken is not null.');
				}

				saveTokens(refreshTokenValue, accessToken);
				if (redirectUrl && redirectUrl.length > 1 && !redirectUrl.includes('/dashboard')) {
					redirectOrChangePage(navigate, redirectUrl);
				} else if (companyId) {
					redirectOrChangePage(navigate, `/consent/${companyId}`);
				} else {
					redirectOrChangePage(navigate, '/candidates');
				}
				return;
			case candidateRole:
				if (refreshTokenValue === null) {
					throw new Error('refreshToken can not be null for Candidates when accessToken is not null.');
				}

				saveTokens(refreshTokenValue, accessToken);
				if (redirectUrl && redirectUrl.length > 1 && !redirectUrl.includes('/candidates')) {
					redirectOrChangePage(navigate, redirectUrl);
				} else {
					redirectOrChangePage(navigate, '/dashboard');
				}
				return;
			default:
				throw new Error(`Unknown role: ${decodedAccessToken.role}.`);
		}
	};

	const goToOtpInput = () => {
		setLoginStep(LoginStep.OTP);
		const interval = setInterval(() => {
			if (otpSeconds <= 0) {
				clearInterval(interval);
				setLoginStep(LoginStep.Login);
				setOtpSeconds(90);
				navigate('/candidates');
				return;
			}
			setOtpSeconds((prev) => prev - 1);
		}, 1000);
	};

	const onCheckBoxClick = (name: string, checked: boolean) => {
		setRememberMe(checked);
	};

	const onOTPSubmit = async () => {
		if (otp.length === 6) {
			setOtpErrorMessage('');
			const authenticationData = await checkOTPCode(email, password, otp);
			handleLogedIn(authenticationData);
		} else {
			setOtpErrorMessage('OTP code is incorrect.');
			handleOTPCodeIncorrect();
		}
	};

	const handleRecaptchaChange = (value: string) => {
		setRecaptchaValue(value);
		if (value !== null) setErrorMessage(null);
	};

	const { lockoutWindowExpires } = userLoginAttempts;
	const currentDate = new Date();

	const showContent = () => {
		if (showCustomerSelection) {
			return (
				<>
					<label className="selectCustomer">Please choose a customer account to login with</label>
					<DropDownList
						data={customers}
						textField="name"
						dataItemKey="id"
						value={customer}
						onChange={handleCustomerChange}
					/>
					<button id="loginWithButton" className="primaryBtn" onClick={handleCustomerClick} type="button">
						<span>Login</span>
					</button>
				</>
			);
		}
		if (lockoutWindowExpires?.getTime() > currentDate.getTime()) {
			return <>{email} Account locked. Please try again in 30 minutes</>;
		}

		return (
			<>
				<label className="loginLabel">Login</label>
				<LoginForm
					username={email}
					password={password}
					remembered={rememberMe}
					buttonText="Login"
					onUsernameChange={handleUsernameChange}
					onPasswordChange={handlePasswordChange}
					onRememberedChange={handleRememberChange}
					onSubmit={onSubmit}
					errorMessage={errorMessage}
					handleRecaptchaChange={handleRecaptchaChange}
					recaptchaRef={captchaRef}
					userLoginAttempts={userLoginAttempts}
					onNext={handleLogin}
					showPassword={showPassword}
				/>
			</>
		);
	};

	const renderLoginStep = () => {
		switch (loginStep) {
			case LoginStep.Login:
				return (
					<div className="loginContainer">
						<ToastContainer autoClose={2500} />
						<AnonymousLayout title="Login" stackedHeader renderHeading logo={logo}>
							{showContent()}
						</AnonymousLayout>
					</div>
				);
			case LoginStep.QRCode:
				return (
					<OtpPageContainer>
						<ToastContainer autoClose={2500} />
						<AnonymousLayout
							title="Scan this QR Code in your authenticator app to link your account"
							stackedHeader
							renderHeading
							logo={logo}
						>
							<OtpQRCodeWrapper>
								<QRCodeSVG value={otpAuthUrl} />
							</OtpQRCodeWrapper>
							<PrimaryButton text="Next" onClick={goToOtpInput} />
						</AnonymousLayout>
					</OtpPageContainer>
				);
			case LoginStep.OTP:
				return (
					<OtpPageContainer>
						<ToastContainer autoClose={2500} />
						<AnonymousLayout
							title="Please enter the verification code from your app"
							stackedHeader
							renderHeading
							logo={logo}
						>
							<OtpInputWrapper
								value={otp}
								onChange={onHandleOTPCodeChange}
								numInputs={6}
								shouldAutoFocus
								containerStyle="otpContainer"
								inputStyle="otpInput"
								isInputNum
							/>
							<PrimaryButton text="Authenticate" onClick={onOTPSubmit} />
							<OtpCheckBoxWrapper>
								<CheckBox
									name="remembered"
									label="Remember this device"
									onChange={onCheckBoxClick}
									value={rememberMe}
								/>
							</OtpCheckBoxWrapper>
							{otpErrorMessage && (
								<ErrorMessageWrapper>
									<p>{otpErrorMessage}</p>
								</ErrorMessageWrapper>
							)}
						</AnonymousLayout>
					</OtpPageContainer>
				);
			default:
				return null;
		}
	};

	return <>{!sending ? renderLoginStep() : sending && <Loading />}</>;
};

const OtpPageContainer = styled.div`
	.pageTitle {
		width: 100%;
		padding-left: 0 !important;
	}

	.otpContainer {
		display: flex;
		justify-content: center;
		margin-bottom: 24px;
	}
`;

const OtpQRCodeWrapper = styled.div`
	margin-bottom: 24px;
`;

const OtpCheckBoxWrapper = styled.div`
	margin-top: 20px;
	input {
		margin-right: 8px;
	}
	label {
		font-size: 15px;
		font-weight: 400;
		line-height: 20px;
	}
`;

const OtpInputWrapper = styled(OtpInput)`
	.otpInput {
		background: #f8f7f3;
		border: 1px solid rgba(38, 38, 38, 0.3);
		border-radius: 8px;
		width: 36px !important;
		height: 36px;
		margin-right: 10px;
	}
`;

const ErrorMessageWrapper = styled.div`
	margin-top: 57px;
	p {
		font-size: 15px;
		line-height: 20px;
		font-weight: 400;
		color: #ec0000;
	}
`;

export default LoginContainer;
